import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { QuickStatsWidget } from './widgets/QuickStatsWidget';
import { TasksTableWidget } from './widgets/TasksTableWidget';
import { ActivityFeedWidget } from './widgets/ActivityFeedWidget';
import { AppPreviewWidget } from './widgets/AppPreviewWidget';
import { AnalyticsWidget } from './AnalyticsWidget';
import { useQuickNotesStore } from '../../store/quick-notes.store';
import { useAppStore } from '../../store/app.store';
import { Eye, Mountain, TrendingUp, Target, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { DailyPlanner } from '../vision/DailyPlanner';
// Helper function to fetch full ranges with all nested data
const fetchFullRanges = async (userId) => {
    try {
        console.log('🔄 fetchFullRanges: Starting fetch for user:', userId);
        // Create a promise that resolves with a timeout (30 seconds for deeply nested data)
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Fetch timeout after 30 seconds')), 30000);
        });
        const fetchPromise = (async () => {
            // Fetch all ranges with initial data
            const { data: rangesData, error: rangesError } = await supabase
                .from('ranges')
                .select('*')
                .eq('user_id', userId)
                .order('position', { ascending: true, nullsFirst: false })
                .order('created_at', { ascending: true });
            if (rangesError) {
                console.error('❌ fetchFullRanges: Error fetching ranges:', rangesError);
                throw rangesError;
            }
            console.log('📊 fetchFullRanges: Found ranges:', rangesData?.length || 0);
            if (!rangesData || rangesData.length === 0) {
                console.log('📭 fetchFullRanges: No ranges found, returning empty array');
                return [];
            }
            const rangesWithData = [];
            // Process all ranges in parallel batches for better performance
            for (const range of rangesData) {
                // Fetch mountains for this range
                const { data: mountainsData, error: mountainsError } = await supabase
                    .from('mountains')
                    .select('*')
                    .eq('range_id', range.id)
                    .order('position', { ascending: true, nullsFirst: false })
                    .order('created_at', { ascending: true });
                if (mountainsError)
                    throw mountainsError;
                const mountainsWithData = [];
                // Process mountains in parallel for each range
                const mountainPromises = (mountainsData || []).map(async (mountain) => {
                    // Fetch hills
                    const { data: hillsData, error: hillsError } = await supabase
                        .from('hills')
                        .select('*')
                        .eq('mountain_id', mountain.id)
                        .order('position', { ascending: true, nullsFirst: false })
                        .order('created_at', { ascending: true });
                    if (hillsError)
                        throw hillsError;
                    const hillsWithData = [];
                    // Process hills in parallel for each mountain
                    const hillPromises = (hillsData || []).map(async (hill) => {
                        // Fetch terrains
                        const { data: terrainsData, error: terrainsError } = await supabase
                            .from('terrains')
                            .select('*')
                            .eq('hill_id', hill.id)
                            .order('created_at', { ascending: true });
                        if (terrainsError)
                            throw terrainsError;
                        const terrainsWithData = [];
                        // Process terrains in parallel for each hill
                        const terrainPromises = (terrainsData || []).map(async (terrain) => {
                            // Fetch lengths
                            const { data: lengthsData, error: lengthsError } = await supabase
                                .from('lengths')
                                .select('*')
                                .eq('terrain_id', terrain.id)
                                .order('created_at', { ascending: true });
                            if (lengthsError)
                                throw lengthsError;
                            const lengthsWithData = [];
                            // Process lengths in parallel for each terrain
                            const lengthPromises = (lengthsData || []).map(async (length) => {
                                // Fetch steps
                                const { data: stepsData, error: stepsError } = await supabase
                                    .from('steps')
                                    .select('*')
                                    .eq('length_id', length.id)
                                    .order('created_at', { ascending: true });
                                if (stepsError)
                                    throw stepsError;
                                return {
                                    ...length,
                                    steps: stepsData || []
                                };
                            });
                            const lengths = await Promise.all(lengthPromises);
                            return {
                                ...terrain,
                                lengths
                            };
                        });
                        const terrains = await Promise.all(terrainPromises);
                        return {
                            ...hill,
                            terrains
                        };
                    });
                    const hills = await Promise.all(hillPromises);
                    return {
                        ...mountain,
                        hills
                    };
                });
                const mountains = await Promise.all(mountainPromises);
                rangesWithData.push({
                    ...range,
                    mountains
                });
            }
            console.log('✅ fetchFullRanges: Successfully loaded', rangesWithData.length, 'ranges with all nested data');
            return rangesWithData;
        })();
        // Race between the fetch and timeout
        return await Promise.race([fetchPromise, timeoutPromise]);
    }
    catch (err) {
        console.error('❌ fetchFullRanges: Error:', err);
        return [];
    }
};
// Placeholder components for widgets that aren't implemented yet
const PlaceholderWidget = ({ title }) => (_jsxs("div", { className: "h-full flex flex-col items-center justify-center text-center p-6", children: [_jsx("div", { className: "w-16 h-16 liquid-glass-interactive rounded-2xl flex items-center justify-center mb-4 border border-white/10", children: _jsx("span", { className: "text-2xl", children: "\uD83D\uDEA7" }) }), _jsx("h3", { className: "text-sm font-semibold text-adaptive-primary mb-2", children: title }), _jsx("p", { className: "text-xs text-adaptive-muted", children: "Coming soon in next update..." }), _jsx("div", { className: "mt-4 px-3 py-1 liquid-glass-interactive rounded-lg border border-white/10", children: _jsx("span", { className: "text-xs text-blue-400", children: "In Development" }) })] }));
const DashboardWidget = () => _jsx(AnalyticsWidget, { type: "overview" });
const TasksCalendarWidget = () => (_jsxs("div", { className: "h-full flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsx("h3", { className: "text-sm font-semibold text-white", children: "December 2024" }), _jsxs("div", { className: "flex gap-1", children: [_jsx("button", { className: "w-6 h-6 text-gray-400 hover:text-white", children: "\u2039" }), _jsx("button", { className: "w-6 h-6 text-gray-400 hover:text-white", children: "\u203A" })] })] }), _jsx("div", { className: "flex-1 bg-white/5 rounded-xl p-3 border border-white/10", children: _jsxs("div", { className: "grid grid-cols-7 gap-1 text-xs", children: [['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (_jsx("div", { className: "text-center text-gray-400 p-1 font-medium", children: day }, day))), Array.from({ length: 35 }, (_, i) => (_jsx("div", { className: `text-center p-1 rounded hover:bg-white/10 cursor-pointer ${i >= 6 && i <= 27 ? 'text-white' : 'text-gray-600'} ${i === 15 ? 'bg-blue-500/20 text-blue-300' : ''}`, children: i >= 6 && i <= 27 ? i - 5 : '' }, i)))] }) })] }));
const RecentBoardsWidget = () => (_jsx("div", { className: "h-full", children: _jsx("div", { className: "space-y-3", children: [
            { name: 'Strategic Planning', color: 'blue', items: 12 },
            { name: 'Development Sprint', color: 'green', items: 8 },
            { name: 'Marketing Campaign', color: 'purple', items: 15 },
        ].map((board, index) => (_jsx("div", { className: "bg-white/5 rounded-lg p-3 border border-white/10 hover:bg-white/10 cursor-pointer transition-all", children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: `w-3 h-3 rounded-full bg-${board.color}-400` }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: "text-sm font-medium text-white", children: board.name }), _jsxs("div", { className: "text-xs text-gray-400", children: [board.items, " items"] })] }), _jsx("div", { className: "text-xs text-gray-400", children: "2h ago" })] }) }, index))) }) }));
const QuickNotesWidget = () => {
    const { currentUser } = useAppStore();
    const { content, setContent, loadNote, updateNote, isSaving } = useQuickNotesStore();
    const [isInitialized, setIsInitialized] = useState(false);
    // Load saved notes on component mount
    useEffect(() => {
        if (currentUser?.id && !isInitialized) {
            loadNote(currentUser.id);
            setIsInitialized(true);
        }
    }, [currentUser?.id, isInitialized, loadNote]);
    const handleChange = (e) => {
        const newContent = e.target.value;
        setContent(newContent);
        // Auto-save to Supabase with debounce
        if (currentUser?.id) {
            // Cancel previous timeout if exists
            if (window.quickNotesSaveTimeout) {
                clearTimeout(window.quickNotesSaveTimeout);
            }
            // Debounce save by 1 second
            window.quickNotesSaveTimeout = setTimeout(() => {
                updateNote(newContent, currentUser.id);
            }, 1000);
        }
    };
    return (_jsxs("div", { className: "h-full relative", children: [_jsx("textarea", { placeholder: "Quick notes and reminders...", className: "w-full h-full bg-transparent border-none text-sm text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-gray-400 resize-none focus:outline-none", value: content, onChange: handleChange }), isSaving && (_jsx("div", { className: "absolute bottom-2 right-2 text-xs text-gray-400", children: "Saving..." }))] }));
};
// Vision Board Widgets
const VisionBoardOverviewWidget = () => {
    const { currentUser } = useAppStore();
    const [ranges, setRanges] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!currentUser?.id)
            return;
        const loadRanges = async () => {
            try {
                const { data, error } = await supabase
                    .from('ranges')
                    .select('id, name, mountains:mountains(*)')
                    .eq('user_id', currentUser.id)
                    .order('position', { ascending: true });
                if (!error && data) {
                    setRanges(data);
                }
            }
            catch (err) {
                console.error('Error loading ranges:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadRanges();
    }, [currentUser?.id]);
    if (loading) {
        return (_jsx("div", { className: "h-full flex items-center justify-center", children: _jsx("div", { className: "text-adaptive-muted", children: "Loading..." }) }));
    }
    return (_jsxs("div", { className: "h-full overflow-y-auto p-4 space-y-3", children: [_jsxs("div", { className: "flex items-center gap-2 mb-4", children: [_jsx(Eye, { className: "w-4 h-4 text-purple-400" }), _jsx("h3", { className: "text-sm font-semibold text-adaptive-primary", children: "Vision Board" })] }), ranges.length === 0 ? (_jsx("div", { className: "text-center py-8", children: _jsx("p", { className: "text-xs text-adaptive-muted", children: "No goals yet. Create one in Vision Board app." }) })) : (ranges.map((range) => (_jsxs("div", { className: "bg-white/5 rounded-lg p-2 border border-white/10", children: [_jsx("div", { className: "text-xs font-semibold text-adaptive-primary mb-1", children: range.name }), _jsxs("div", { className: "text-xs text-adaptive-muted", children: [range.mountains?.length || 0, " goals"] })] }, range.id))))] }));
};
const VisionMountainsListWidget = () => {
    const { currentUser } = useAppStore();
    const [mountains, setMountains] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!currentUser?.id)
            return;
        const loadMountains = async () => {
            try {
                const { data, error } = await supabase
                    .from('mountains')
                    .select('id, name, completed, hills:hills(*)')
                    .eq('user_id', currentUser.id)
                    .order('created_at', { ascending: false })
                    .limit(10);
                if (!error && data) {
                    setMountains(data);
                }
            }
            catch (err) {
                console.error('Error loading mountains:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadMountains();
    }, [currentUser?.id]);
    const getCompletionPercentage = (mountain) => {
        if (!mountain.hills || mountain.hills.length === 0)
            return 0;
        const completed = mountain.hills.filter((h) => h.completed).length;
        return Math.round((completed / mountain.hills.length) * 100);
    };
    if (loading) {
        return (_jsx("div", { className: "h-full flex items-center justify-center", children: _jsx("div", { className: "text-adaptive-muted", children: "Loading..." }) }));
    }
    return (_jsxs("div", { className: "h-full overflow-y-auto p-4 space-y-2", children: [_jsxs("div", { className: "flex items-center gap-2 mb-3", children: [_jsx(Mountain, { className: "w-4 h-4 text-orange-400" }), _jsx("h3", { className: "text-sm font-semibold text-adaptive-primary", children: "Mountains & Goals" })] }), mountains.length === 0 ? (_jsx("div", { className: "text-center py-8", children: _jsx("p", { className: "text-xs text-adaptive-muted", children: "No goals created yet." }) })) : (mountains.map((mountain) => {
                const percentage = getCompletionPercentage(mountain);
                return (_jsxs("div", { className: "bg-white/5 rounded p-2 border border-white/10", children: [_jsxs("div", { className: "flex items-start justify-between gap-2 mb-1", children: [_jsx("span", { className: `text-xs font-medium ${mountain.completed ? 'line-through text-adaptive-muted' : 'text-adaptive-primary'}`, children: mountain.name }), mountain.completed && _jsx(CheckCircle, { className: "w-3 h-3 text-green-400 flex-shrink-0" })] }), _jsx("div", { className: "w-full h-1 bg-white/10 rounded-full overflow-hidden", children: _jsx("div", { className: "h-full bg-gradient-to-r from-orange-400 to-red-400 transition-all", style: { width: `${percentage}%` } }) }), _jsxs("div", { className: "text-xs text-adaptive-muted mt-1", children: [percentage, "% complete"] })] }, mountain.id));
            }))] }));
};
const VisionGoalsSummaryWidget = () => {
    const { currentUser } = useAppStore();
    const [stats, setStats] = useState({ total: 0, completed: 0, inProgress: 0 });
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!currentUser?.id)
            return;
        const loadStats = async () => {
            try {
                const { data, error } = await supabase
                    .from('mountains')
                    .select('completed')
                    .eq('user_id', currentUser.id);
                if (!error && data) {
                    const total = data.length;
                    const completed = data.filter((m) => m.completed).length;
                    setStats({
                        total,
                        completed,
                        inProgress: total - completed
                    });
                }
            }
            catch (err) {
                console.error('Error loading stats:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadStats();
    }, [currentUser?.id]);
    if (loading) {
        return _jsx("div", { className: "h-full flex items-center justify-center", children: _jsx("div", { className: "text-adaptive-muted", children: "Loading..." }) });
    }
    return (_jsxs("div", { className: "h-full p-4 flex flex-col gap-3", children: [_jsxs("div", { className: "text-xs font-semibold text-adaptive-primary flex items-center gap-2", children: [_jsx(TrendingUp, { className: "w-4 h-4 text-blue-400" }), "Goals Summary"] }), _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "bg-blue-500/10 rounded p-2 border border-blue-500/20", children: [_jsx("div", { className: "text-xs text-blue-300", children: "Total Goals" }), _jsx("div", { className: "text-lg font-bold text-blue-400", children: stats.total })] }), _jsxs("div", { className: "bg-green-500/10 rounded p-2 border border-green-500/20", children: [_jsx("div", { className: "text-xs text-green-300", children: "Completed" }), _jsx("div", { className: "text-lg font-bold text-green-400", children: stats.completed })] }), _jsxs("div", { className: "bg-orange-500/10 rounded p-2 border border-orange-500/20", children: [_jsx("div", { className: "text-xs text-orange-300", children: "In Progress" }), _jsx("div", { className: "text-lg font-bold text-orange-400", children: stats.inProgress })] })] })] }));
};
const VisionMountainDetailWidget = () => {
    const { currentUser } = useAppStore();
    const [mountain, setMountain] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!currentUser?.id)
            return;
        const loadLatestMountain = async () => {
            try {
                const { data, error } = await supabase
                    .from('mountains')
                    .select('id, name, completed, hills:hills(id, name, completed, terrains:terrains(id, name, completed, lengths:lengths(id, name, completed, steps:steps(id, label, completed))))')
                    .eq('user_id', currentUser.id)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .single();
                if (!error && data) {
                    setMountain(data);
                }
            }
            catch (err) {
                console.error('Error loading mountain:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadLatestMountain();
    }, [currentUser?.id]);
    if (loading) {
        return _jsx("div", { className: "h-full flex items-center justify-center", children: _jsx("div", { className: "text-adaptive-muted", children: "Loading..." }) });
    }
    if (!mountain) {
        return (_jsxs("div", { className: "h-full flex flex-col items-center justify-center text-center p-4", children: [_jsx(Target, { className: "w-8 h-8 text-purple-400 mb-2" }), _jsx("p", { className: "text-xs text-adaptive-muted", children: "Create a goal to see details here" })] }));
    }
    const totalSteps = mountain.hills?.reduce((acc, hill) => {
        return acc + (hill.terrains?.reduce((tacc, terrain) => {
            return tacc + (terrain.lengths?.reduce((lacc, length) => {
                return lacc + (length.steps?.length || 0);
            }, 0) || 0);
        }, 0) || 0);
    }, 0) || 0;
    const completedSteps = mountain.hills?.reduce((acc, hill) => {
        return acc + (hill.terrains?.reduce((tacc, terrain) => {
            return tacc + (terrain.lengths?.reduce((lacc, length) => {
                return lacc + (length.steps?.filter((s) => s.completed).length || 0);
            }, 0) || 0);
        }, 0) || 0);
    }, 0) || 0;
    return (_jsxs("div", { className: "h-full overflow-y-auto p-4 space-y-3", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Target, { className: "w-4 h-4 text-purple-400" }), _jsx("h3", { className: "text-sm font-semibold text-adaptive-primary truncate", children: mountain.name }), mountain.completed && _jsx(CheckCircle, { className: "w-4 h-4 text-green-400 flex-shrink-0" })] }), _jsxs("div", { className: "space-y-1", children: [_jsxs("div", { className: "flex justify-between items-center", children: [_jsx("span", { className: "text-xs text-adaptive-muted", children: "Progress" }), _jsxs("span", { className: "text-xs font-semibold text-adaptive-secondary", children: [completedSteps, "/", totalSteps] })] }), _jsx("div", { className: "w-full h-2 bg-white/10 rounded-full overflow-hidden", children: _jsx("div", { className: "h-full bg-gradient-to-r from-purple-400 to-purple-600 transition-all", style: { width: totalSteps > 0 ? `${(completedSteps / totalSteps) * 100}%` : '0%' } }) })] }), _jsxs("div", { className: "text-xs text-adaptive-muted", children: [mountain.hills?.length || 0, " sub-goals"] })] }));
};
const VisionDailyPlanWidget = () => {
    const { currentUser } = useAppStore();
    const [ranges, setRanges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!currentUser?.id) {
            setIsLoading(false);
            return;
        }
        console.log('🎯 VisionDailyPlanWidget: Starting to load ranges for user:', currentUser.id);
        let isMounted = true;
        const loadRanges = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const rangesData = await fetchFullRanges(currentUser.id);
                console.log('🎯 VisionDailyPlanWidget: Successfully loaded ranges:', {
                    count: rangesData.length,
                    ranges: rangesData.map(r => ({ id: r.id, name: r.name, mountainCount: r.mountains?.length || 0 }))
                });
                if (isMounted) {
                    setRanges(rangesData);
                    if (rangesData.length === 0) {
                        console.warn('⚠️ VisionDailyPlanWidget: No ranges found for this user. They may need to create goals in the Vision Board app first.');
                    }
                }
            }
            catch (err) {
                console.error('❌ VisionDailyPlanWidget: Error loading ranges:', err);
                if (isMounted) {
                    setRanges([]);
                    setError(err?.message || 'Failed to load ranges');
                }
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        loadRanges();
        return () => {
            isMounted = false;
        };
    }, [currentUser?.id]);
    if (!currentUser?.id) {
        return (_jsx("div", { className: "w-full h-full flex items-center justify-center", children: _jsx("div", { className: "text-adaptive-muted text-sm", children: "Not authenticated" }) }));
    }
    if (isLoading) {
        return (_jsx("div", { className: "w-full h-full flex items-center justify-center", children: _jsx("div", { className: "text-adaptive-muted text-sm", children: "Loading vision goals..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: "w-full h-full flex items-center justify-center", children: _jsxs("div", { className: "text-red-400 text-sm", children: ["Error: ", error] }) }));
    }
    if (ranges.length === 0) {
        return (_jsxs("div", { className: "w-full h-full flex flex-col overflow-y-auto", children: [_jsx("div", { className: "p-4", children: _jsx("p", { className: "text-xs text-adaptive-muted mb-3", children: "No vision goals found. Create some in the Vision Board app to use the Daily Planner." }) }), _jsx(DailyPlanner, { ranges: [], userId: currentUser.id, compactMode: true })] }));
    }
    // Render with loaded ranges
    return (_jsx("div", { className: "w-full h-full flex flex-col overflow-y-auto", children: _jsx(DailyPlanner, { ranges: ranges, userId: currentUser.id, compactMode: true }) }));
};
export const WidgetRegistry = {
    'dashboard': DashboardWidget,
    'tasks-table': TasksTableWidget,
    'tasks-calendar': TasksCalendarWidget,
    'quick-stats': QuickStatsWidget,
    'activity-feed': ActivityFeedWidget,
    'recent-boards': RecentBoardsWidget,
    'team-overview': () => _jsx(PlaceholderWidget, { title: "Team Overview" }),
    'goals-tracker': () => _jsx(PlaceholderWidget, { title: "Goals Tracker" }),
    'time-tracker': () => _jsx(PlaceholderWidget, { title: "Time Tracker" }),
    'notifications': () => _jsx(PlaceholderWidget, { title: "Notifications" }),
    'weather': () => _jsx(PlaceholderWidget, { title: "Weather" }),
    'clock': () => _jsx(PlaceholderWidget, { title: "World Clock" }),
    'notes': QuickNotesWidget,
    'bookmarks': () => _jsx(PlaceholderWidget, { title: "Bookmarks" }),
    'app-widget': AppPreviewWidget,
    'performance-metrics': () => _jsx(AnalyticsWidget, { type: "performance" }),
    'revenue-chart': () => _jsx(AnalyticsWidget, { type: "revenue" }),
    'engagement-stats': () => _jsx(AnalyticsWidget, { type: "engagement" }),
    'vision-board-overview': VisionBoardOverviewWidget,
    'vision-mountains-list': VisionMountainsListWidget,
    'vision-goals-summary': VisionGoalsSummaryWidget,
    'vision-mountain-detail': VisionMountainDetailWidget,
    'vision-daily-plan': VisionDailyPlanWidget,
};
export function renderWidget(type, props = {}) {
    const WidgetComponent = WidgetRegistry[type];
    return _jsx(WidgetComponent, { ...props });
}
